import type { MerchantCategoryCodeSummary, MccSuggestion } from '@ballerine/common';
import { CreditCard } from 'lucide-react';

import { CopyButton } from '@/components';
import { ReportWrapper } from '../../report-shared/components';
import { ctw } from '@/common/utils/ctw';
import { Separator } from '@/components/atoms/Separator';

type MerchantCategoryCodesSectionProps = {
  mccSummary: MerchantCategoryCodeSummary | null | undefined;
};

const formatMccDisplay = (code?: string | null, description?: string | null) =>
  [code, description].filter(Boolean).join(' - ');

export const MerchantCategoryCodesSection = ({ mccSummary }: MerchantCategoryCodesSectionProps) => {
  if (!mccSummary?.primary?.mcc) {
    return null;
  }

  const { primary, alternatives } = mccSummary;

  return (
    <ReportWrapper
      title="Merchant Category Codes"
      Icon={CreditCard}
      tooltip="Primary MCC assignment with supporting reasoning and alternative options."
      showTooltip={false}
    >
      <p className="-mt-3">Based on Visa Merchant Data Standards Manual</p>
      <div className={ctw('grid grid-cols-1', alternatives?.length && 'grid-cols-2 gap-6')}>
        <div>
          <p className="text-sm font-medium tracking-tight uppercase text-blue-600 bg-blue-600/20 rounded-sm py-0.5 px-2 inline-block mb-4 mt-[1.35rem]">
            Primary
          </p>
          <MCCItem {...primary} />
        </div>

        {alternatives?.length ? (
          <div className="p-5 border rounded-lg space-y-4">
            <div className="text-sm font-medium tracking-tight uppercase text-slate-400 bg-slate-400/20 rounded-sm py-0.5 px-2 inline-block">
              Alternatives
            </div>
            <div className="space-y-4">
              {alternatives.map((alternative, i) => (
                <>
                  <MCCItem
                    key={formatMccDisplay(alternative.mcc, alternative.description)}
                    {...alternative}
                  />
                  {i < alternatives.length - 1 && <Separator />}
                </>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </ReportWrapper>
  );
};

const MCCItem = ({ mcc, description, reasoning }: MccSuggestion) => {
  return (
    <div>
      <p className="text-xl font-semibold text-slate-900">{mcc}</p>

      {description && (
        <div className="space-x-1 text-justify text-sm leading-relaxed">
          <span className="font-medium text-sm text-slate-900">{description}</span>
          <CopyButton className="inline" textToCopy={formatMccDisplay(mcc, description)} />
        </div>
      )}

      {reasoning && (
        <p className="text-sm text-neutral-700 whitespace-pre-line text-justify leading-relaxed">
          {reasoning}
        </p>
      )}
    </div>
  );
};
